import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Container,
  Typography,
  Card,
  Stack,
  Button,
  Box,
  Alert,
  TextField,
  MenuItem,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { getTypography } from './typographyApi';
import SitePreviewFrame, { PREVIEW_PAGES } from '../../components/typography/SitePreviewFrame';
import TypeSpecimenPreview from '../../components/typography/TypeSpecimenPreview';
import { readEditorPayload, isEditorPayloadEvent } from '../../utils/typographyPreview';
import { ROLE_GROUP_COLORS } from '../../components/typography/roleLabels';

// Full-size real-page preview for /dashboard/typography. Opened from the editor
// (which keeps it in sync with unsaved edits via utils/typographyPreview.js), or
// directly to review the saved draft against what's published.

const SOURCE_LABEL = {
  editor: 'Unsaved edits from the editor',
  draft: 'Saved draft',
  published: 'Published (live now)',
  defaults: "Marketing's defaults",
};

const SAMPLE = 'sample';

// Normalize what the user typed into a site path: "/va", "va", or a full URL.
const toPath = (raw) => {
  const v = (raw || '').trim();
  if (!v) return '/home';
  try {
    const u = new URL(v, window.location.origin);
    return u.origin === window.location.origin ? u.pathname + u.search : '/home';
  } catch (e) {
    return v.startsWith('/') ? v : `/${v}`;
  }
};

export default function TypographyPreview() {
  const navigate = useNavigate();
  const [params] = useSearchParams();

  const [server, setServer] = useState(null); // { draft, published, defaults } — each { roles, groups }
  const [editor, setEditor] = useState(() => readEditorPayload());
  const [source, setSource] = useState(null);
  const [path, setPath] = useState(params.get('path') || '/home');
  const [customPath, setCustomPath] = useState('');
  const [device, setDevice] = useState(params.get('device') === 'mobile' ? 'mobile' : 'desktop');
  const [errors, setErrors] = useState([]);
  const [loadError, setLoadError] = useState('');
  const [loading, setLoading] = useState(true);
  const [roleRegistry, setRoleRegistry] = useState([]);
  const [showLabels, setShowLabels] = useState(true);

  // Load saved values; start with the most relevant source.
  useEffect(() => {
    (async () => {
      const res = await getTypography();
      if (res?.status === 200) {
        const s = {
          draft: res.data.draft,
          published: res.data.published,
          defaults: { roles: res.data.defaults, groups: {} },
        };
        setServer(s);
        setRoleRegistry(res.data.roles || []);
        setSource(readEditorPayload() ? 'editor' : s.draft ? 'draft' : s.published ? 'published' : 'defaults');
      } else {
        setLoadError(res?.data?.message || 'Could not load typography settings');
      }
      setLoading(false);
    })();
  }, []);

  // Live updates while the editor tab is open.
  useEffect(() => {
    const onStorage = (e) => {
      if (isEditorPayloadEvent(e)) setEditor(readEditorPayload());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // { roles, groups } for the chosen source.
  const settings = useMemo(() => {
    if (!server || !source) return null;
    if (source === 'editor') return editor?.settings || null;
    return server[source] || null;
  }, [server, source, editor]);

  const sources = useMemo(() => {
    if (!server) return [];
    return ['editor', 'draft', 'published', 'defaults'].filter((k) => (k === 'editor' ? !!editor : !!server[k]));
  }, [server, editor]);

  const goCustom = () => {
    const p = toPath(customPath);
    setPath(p);
    setCustomPath(p);
  };

  if (loading) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const pickerValue = path === SAMPLE || PREVIEW_PAGES.some((p) => p.path === path) ? path : '';

  return (
    <Container maxWidth="xl">
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} mb={2}>
        <Box>
          <Typography variant="h4">Typography preview</Typography>
          <Typography variant="body2" color="text.secondary">
            Real site pages with unpublished font settings applied. Nothing here changes the live site.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/dashboard/typography')}>
          Back to editor
        </Button>
      </Stack>

      <Card sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
          <TextField
            select
            size="small"
            label="Showing"
            value={source || ''}
            onChange={(e) => setSource(e.target.value)}
            sx={{ minWidth: 260 }}
            disabled={!sources.length}
          >
            {sources.map((k) => (
              <MenuItem key={k} value={k}>
                {SOURCE_LABEL[k]}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Page"
            value={pickerValue}
            onChange={(e) => {
              setPath(e.target.value);
              setCustomPath('');
            }}
            sx={{ minWidth: 220 }}
          >
            {PREVIEW_PAGES.map((p) => (
              <MenuItem key={p.path} value={p.path}>
                {p.label}
              </MenuItem>
            ))}
            <MenuItem value={SAMPLE}>All-roles sample</MenuItem>
          </TextField>
          <Stack direction="row" spacing={1} alignItems="center">
            <TextField
              size="small"
              label="Or any page path"
              placeholder="/fha"
              value={customPath}
              onChange={(e) => setCustomPath(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && goCustom()}
              sx={{ width: 200 }}
            />
            <Button variant="text" onClick={goCustom} disabled={!customPath.trim()}>
              Go
            </Button>
          </Stack>
          <Box sx={{ flexGrow: 1 }} />
          <FormControlLabel
            control={<Switch size="small" checked={showLabels} onChange={(e) => setShowLabels(e.target.checked)} />}
            label="Show labels"
          />
          <ToggleButtonGroup size="small" exclusive value={device} onChange={(_, v) => v && setDevice(v)}>
            <ToggleButton value="desktop" sx={{ textTransform: 'none' }}>
              Desktop
            </ToggleButton>
            <ToggleButton value="mobile" sx={{ textTransform: 'none' }}>
              Mobile
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>
        {showLabels && path !== SAMPLE && (
          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
            {Object.entries(ROLE_GROUP_COLORS).map(([name, color]) => (
              <Stack key={name} direction="row" spacing={0.5} alignItems="center">
                <Box sx={{ width: 10, height: 10, borderRadius: '2px', bgcolor: color }} />
                <Typography variant="caption" color="text.secondary">
                  {name}
                </Typography>
              </Stack>
            ))}
          </Stack>
        )}
        {source === 'editor' && editor && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            Updates live as you edit in the Typography editor · last change {new Date(editor.updatedAt).toLocaleTimeString()}
          </Typography>
        )}
      </Card>

      {loadError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Couldn&apos;t load the typography settings ({loadError}). The page below shows the live site without preview values.
        </Alert>
      )}

      {!loadError && errors.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Some values can&apos;t be previewed yet — showing the last valid version. {errors[0]}
          {errors.length > 1 ? ` (+${errors.length - 1} more)` : ''}
        </Alert>
      )}

      <Box sx={{ mb: 4 }}>
        {path === SAMPLE ? (
          settings && (
            <TypeSpecimenPreview roles={settings.roles} defaults={server?.defaults?.roles || {}} device={device} />
          )
        ) : (
          <SitePreviewFrame
            settings={settings}
            path={path}
            device={device}
            onErrors={setErrors}
            showLabels={showLabels}
            roles={roleRegistry}
          />
        )}
      </Box>
    </Container>
  );
}
